import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const SidebarContext = createContext({
    expanded: false,
    toggleExpanded: () => { },
    mobileOpen: false,
    setMobileOpen: () => { },
});

export function useSidebar() {
    return useContext(SidebarContext);
}

export function Sidebar({ children, forceCollapsed = false, mobileHeader }) {
    // Toggle por clique — sem hover, sem lock, sem instabilidade
    const [expanded, setExpanded] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleExpanded = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    // Close mobile menu on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setMobileOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isExpanded = forceCollapsed ? false : (mobileOpen ? true : expanded);

    return (
        <SidebarContext.Provider value={{ expanded: isExpanded, toggleExpanded, forceCollapsed, mobileOpen, setMobileOpen }}>
            {/* Mobile Header (rendered outside aside to always be visible on mobile) */}
            {mobileHeader}

            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                data-collapsed={!isExpanded}
                className={cn(
                    "group h-screen transition-all duration-300 border-r border-sidebar-border bg-sidebar flex flex-col z-50 fixed left-0 top-0",
                    isExpanded ? "w-64" : "w-16",
                    "hidden md:flex",
                    mobileOpen && "!flex w-64"
                )}
            >
                {children}
            </aside>

            {/* Spacer — acompanha o estado real da sidebar */}
            <div className={cn("transition-all duration-300 hidden md:block", isExpanded ? "w-64" : "w-16")} />
        </SidebarContext.Provider>
    );
}

/**
 * Botão de toggle para expandir/colapsar a sidebar.
 * Renderiza no footer da sidebar automaticamente.
 */
export function SidebarToggle() {
    const { expanded, toggleExpanded, forceCollapsed } = useContext(SidebarContext);

    if (forceCollapsed) return null;

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={toggleExpanded}
                        className={cn(
                            "flex items-center rounded-lg px-3 py-2 cursor-pointer transition-colors text-foreground-muted hover:bg-sidebar-hover hover:text-foreground w-full",
                            !expanded && "justify-center px-2"
                        )}
                    >
                        {expanded ? (
                            <>
                                <PanelLeftClose className="w-4 h-4 shrink-0" />
                                <span className="font-medium ml-3 text-sm">Recolher</span>
                            </>
                        ) : (
                            <PanelLeft className="w-4 h-4 shrink-0" />
                        )}
                    </button>
                </TooltipTrigger>
                {!expanded && (
                    <TooltipContent side="right" sideOffset={8} className="bg-surface border-border-strong text-foreground-strong z-[9999]">
                        Expandir menu
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
}

// Header should just have the logo, no text
export function SidebarHeader({ children, className }) {
    const { expanded } = useContext(SidebarContext);

    // If children are provided (like the AccountSwitcher), render them.
    // However, if the sidebar is collapsed, we might want to force the logo view or render the children in a simplified way.
    // For now, let's assume the passed children (AccountSwitcher) handles its own responsive/collapsed state or we just return children.

    if (children) {
        return (
            <div className={cn("h-16 flex items-center border-b border-sidebar-border", className)}>
                {children}
            </div>
        );
    }

    return (
        <div className={cn("h-16 flex items-center justify-center border-b border-sidebar-border", className)}>
            <div className="w-8 h-8 rounded-lg bg-surface-active flex items-center justify-center shrink-0 shadow-sm border border-border-strong">
                <Lock className="text-foreground-muted w-4 h-4" />
            </div>
        </div>
    );
}

export function SidebarContent({ children }) {
    return (
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 flex flex-col gap-2 px-2 custom-scrollbar">
            {children}
        </div>
    );
}

export function SidebarFooter({ children }) {
    return (
        <div className="p-2">
            {children}
        </div>
    );
}

/**
 * O item ativo é preenchido; os outros são contorno.
 *
 * O Lucide é um conjunto só de traço — não existe variante sólida, e mandar
 * `fill` sozinho não resolve: Kanban e Relatórios são linhas abertas, sem área
 * para preencher, e ficariam idênticos ao estado inativo enquanto os vizinhos
 * mudavam. Por isso o preenchimento vem em três camadas que funcionam em
 * qualquer glifo — o fundo do item, o traço mais grosso e um `fill` de baixa
 * opacidade que só as formas fechadas aproveitam.
 *
 * A borda existe nos DOIS estados (transparente quando inativo) porque ela
 * ocupa 1px: só no ativo, o item pulava um pixel ao ser selecionado.
 */
const ICON_FILL_OPACITY = 0.18;

export function SidebarItem({ icon: Icon, label, to, active, alert, onClick, className }) {
    const { expanded } = useContext(SidebarContext);

    const content = (
        <div className={cn(
            "group/nav flex items-center rounded-lg px-3 py-2 cursor-pointer relative",
            "border transition-colors duration-200 ease-out",
            active
                ? "bg-cyan-500/10 border-cyan-500/25 text-cyan-700 dark:text-cyan-300 shadow-sm"
                : "border-transparent text-foreground-muted hover:bg-sidebar-hover hover:text-foreground",
            !expanded && "justify-center px-2",
            className
        )}>
            <Icon
                className={cn(
                    "w-4 h-4 shrink-0 transition-transform duration-200 ease-out",
                    active ? "animate-nav-icon-pop" : "group-hover/nav:scale-110"
                )}
                strokeWidth={active ? 2.25 : 2}
                fill={active ? "currentColor" : "none"}
                fillOpacity={active ? ICON_FILL_OPACITY : 0}
            />

            <span className={cn(
                "font-medium transition-all duration-300 overflow-hidden whitespace-nowrap",
                expanded ? "opacity-100 max-w-full ml-3" : "opacity-0 max-w-0"
            )}>
                {label}
            </span>
        </div>
    );

    // Wrap with Radix Tooltip for collapsed state
    const wrappedContent = !expanded ? (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    {content}
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} className="bg-surface border-border-strong text-foreground-strong z-[9999]">
                    {label}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    ) : content;

    if (onClick) {
        return <div onClick={onClick} className="w-full text-left bg-transparent border-0">{wrappedContent}</div>;
    }

    return (
        <Link to={to}>
            {wrappedContent}
        </Link>
    );
}

// SidebarTrigger removido — sidebar expande apenas por hover, sem pin

/**
 * Wrapper around any component with onOpenChange (DropdownMenu, Popover, etc.)
 * A sidebar colapsa automaticamente ao sair do hover — menus são portals 
 * e permanecem visíveis independentemente.
 */
export function SidebarLock({ as: Component, onOpenChange, children, ...props }) {
    return <Component onOpenChange={onOpenChange} {...props}>{children}</Component>;
}

export default function InternalSidebar() {
    const navigate = useNavigate();
    const location = useLocation();

    // We define expanded state in the layout usage or internally here if we want to replace the whole Sidebar component
    // But for this custom implementation, let's just export the composition
    return null;
}
